import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, ExternalLink, Map } from 'lucide-react';

const LocationMapsSection: React.FC = () => {
  const [activeMapType, setActiveMapType] = useState<'google' | 'leaflet'>('google');
  const leafletMapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const leafletLoadedRef = useRef<boolean>(false);

  // Memoize static location data
  const locationData = useMemo(() => ({
    name: "Desa Karangmalang",
    fullAddress: "Karangmalang, Kec. Bobotsari, Kabupaten Purbalingga, Jawa Tengah",
    coordinates: {
      lat: -7.3137961,
      lng: 109.3917051
    },
    googleMapsUrl: "https://www.google.com/maps/place/Karangmalang,+Kec.+Bobotsari,+Kabupaten+Purbalingga,+Jawa+Tengah/@-7.314095,109.3898099,15z/data=!3m1!4b1!4m6!3m5!1s0x2e6ff82e222a2cab:0x5027a76e3551400!8m2!3d-7.3137961!4d109.3917051!16s%2Fg%2F1233fm__?entry=ttu&g_ep=EgoyMDI1MDUyMS4wIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
    embedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15792.841234567891!2d109.3898099!3d-7.314095!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e6ff82e222a2cab%3A0x5027a76e3551400!2sKarangmalang%2C%20Kec.%20Bobotsari%2C%20Kabupaten%20Purbalingga%2C%20Jawa%20Tengah!5e0!3m2!1sen!2sid!4v1234567890!5m2!1sen!2sid"
  }), []);

  // Memoize map types configuration
  const mapTypes = useMemo(() => [
    {
      id: 'google' as const,
      name: 'Google Maps',
      icon: Map,
      description: 'View with satellite imagery and detailed street information'
    },
    {
      id: 'leaflet' as const,
      name: 'Leaflet Maps',
      icon: Navigation,
      description: 'Interactive open-source mapping with custom markers'
    }
  ], []);

  // Memoize location features
  const locationFeatures = useMemo(() => [
    {
      icon: MapPin,
      title: "Lokasi Strategis",
      description: "Terletak di Kecamatan Bobotsari, mudah diakses dari berbagai arah"
    },
    {
      icon: Navigation,
      title: "Koordinat GPS",
      description: `${locationData.coordinates.lat}, ${locationData.coordinates.lng}`
    },
    {
      icon: ExternalLink,
      title: "Akses Digital",
      description: "Tersedia di Google Maps dan platform navigasi lainnya"
    }
  ], [locationData.coordinates]);

  // Optimized Leaflet loader with caching
  const loadLeaflet = useCallback(async () => {
    if (leafletLoadedRef.current) return;

    // Load CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Load JS
    if (!(window as any).L) {
      return new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
          leafletLoadedRef.current = true;
          resolve();
        };
        document.head.appendChild(script);
      });
    }
    leafletLoadedRef.current = true;
  }, []);

  // Simplified map initialization - no boundary fetching
  const initializeLeafletMap = useCallback(async () => {
    if (!leafletMapRef.current || mapInstanceRef.current) return;

    await loadLeaflet();
    const L = (window as any).L;
    
    if (!L) return;

    // Initialize map
    const map = L.map(leafletMapRef.current).setView(
      [locationData.coordinates.lat, locationData.coordinates.lng], 
      14
    );

    // Add tile layers
    const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    });

    osmLayer.addTo(map);

    // Simplified custom marker
    const customIcon = L.divIcon({
      html: `
        <div style="
          background: linear-gradient(135deg, #3b82f6, #10b981);
          width: 32px;
          height: 32px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg style="transform: rotate(45deg); width: 18px; height: 18px; fill: white;" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </div>
      `,
      className: 'custom-marker',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });

    // Add marker
    const marker = L.marker([locationData.coordinates.lat, locationData.coordinates.lng], {
      icon: customIcon
    }).addTo(map);

    marker.bindPopup(`
      <div style="text-align: center; font-family: system-ui; min-width: 200px;">
        <h3 style="margin: 0 0 8px 0; color: #1f2937; font-size: 16px;">${locationData.name}</h3>
        <p style="margin: 0 0 8px 0; color: #6b7280; font-size: 12px; line-height: 1.4;">${locationData.fullAddress}</p>
        <div style="background: linear-gradient(135deg, #3b82f6, #10b981); color: white; padding: 4px 8px; border-radius: 12px; font-size: 11px; display: inline-block;">
          📍 KKN UNSOED 2025
        </div>
      </div>
    `);

    // Add simple area circle
    L.circle([locationData.coordinates.lat, locationData.coordinates.lng], {
      color: '#10b981',
      fillColor: '#10b981',
      fillOpacity: 0.1,
      radius: 2000,
      weight: 2,
      dashArray: '5, 10'
    }).addTo(map);

    mapInstanceRef.current = map;
  }, [locationData, loadLeaflet]);

  // Initialize Leaflet Map effect - optimized
  useEffect(() => {
    if (activeMapType === 'leaflet') {
      initializeLeafletMap();
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [activeMapType, initializeLeafletMap]);

  // Memoized map selector handler
  const handleMapTypeChange = useCallback((mapType: 'google' | 'leaflet') => {
    setActiveMapType(mapType);
  }, []);

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 via-white to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Simplified Header */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent mb-6">
            Lokasi KKN
          </h2>
          <div className="h-1 bg-gradient-to-r from-blue-400 to-emerald-500 rounded-full mx-auto mb-8 w-32 sm:w-48" />
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Temukan lokasi Desa Karangmalang tempat berlangsungnya program KKN UNSOED 2025
          </p>
        </motion.div>

        {/* Static Location Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {locationFeatures.map((feature, index) => (
            <div
              key={feature.title}
              className="group bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-200/50 dark:border-slate-700/50 hover:shadow-xl transition-all duration-200"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-emerald-500 text-white rounded-lg">
                  <feature.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-white mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Simplified Map Type Selector */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-2 shadow-lg border border-gray-200/50 dark:border-slate-700/50">
            {mapTypes.map((mapType) => (
              <button
                key={mapType.id}
                onClick={() => handleMapTypeChange(mapType.id)}
                className={`
                  relative flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200
                  ${activeMapType === mapType.id
                    ? 'bg-gradient-to-r from-blue-500 to-emerald-500 text-white shadow-lg'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                  }
                `}
              >
                <mapType.icon className="w-5 h-5" />
                <span className="font-medium">{mapType.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Static Maps Container */}
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-200/50 dark:border-slate-700/50">
          {/* Location Header */}
          <div className="text-center mb-8">
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white mb-2">
              {locationData.name}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {locationData.fullAddress}
            </p>
            <a
              href={locationData.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="font-medium">Buka di Google Maps</span>
            </a>
          </div>

          {/* Simplified Map Display */}
          <div className="relative w-full h-96 lg:h-[500px] rounded-2xl overflow-hidden shadow-lg">
            {activeMapType === 'google' ? (
              <iframe
                src={locationData.embedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Google Maps - Desa Karangmalang"
                className="rounded-2xl"
              />
            ) : (
              <div className="w-full h-full relative">
                <div 
                  ref={leafletMapRef}
                  className="w-full h-full rounded-2xl"
                  style={{ minHeight: '400px' }}
                />
                {!mapInstanceRef.current && (
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-emerald-100 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center rounded-2xl">
                    <div className="text-center text-gray-600 dark:text-gray-400">
                      <Navigation className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                      <h4 className="text-xl font-bold mb-2">Loading Leaflet Maps...</h4>
                      <p className="text-sm">Interactive map sedang dimuat</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Map Description */}
          <div className="mt-6 text-center">
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {activeMapType === 'leaflet' 
                ? "Interactive open-source mapping with custom markers"
                : mapTypes.find(type => type.id === activeMapType)?.description
              }
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 text-sm">
            <MapPin className="w-4 h-4" />
            <span>
              Gunakan aplikasi navigasi favorit Anda dengan koordinat{' '}
              <span className="font-mono bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded">
                {locationData.coordinates.lat}, {locationData.coordinates.lng}
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Memoize the entire component
export default memo(LocationMapsSection);